// shiftSummary.js - Shift Summary modal for Personnel Metrics.
// Shows current-shift workload, completion breakdown
// station movement, and active vs idle time split.
//
// ── Data flow ─────────────────────────────────────────────────────────────────
//
// Now:         useShiftSummary(person.employeeNumber) → live fetch from
//              RTLS_PersonnelMetricsShiftSum + RTLS_stationsVisited
//              via dataReceiver → dataServe → Azure SQL
//
// The person prop is still required for the header (fullName, employeeNumber,
// department). shiftSummary data is fetched independently so the parent
// component does not need to pre-load or pass shift state down.
//
// ── Loading / error states ───────────────────────────────────────────────────
//
//   loading  Skeleton band placeholders preserve the modal's layout so
//            the content doesn't jump when data arrives.
//
//   error    Inline error message inside the modal body. Does not crash
//            the parent or close the modal — the user can dismiss normally.
//
//   empty    stationsVisited: [] renders a "None recorded" fallback so
//            the Stations Visited section is never blank.
//
// ── completionPct ────────────────────────────────────────────────────────────
//
// Previously derived from s.jobsAssigned (not in the live API response).
// The API returns jobsCompleted + jobsInProgress; total assigned is their
// sum. completionPct is recalculated from that derived total.
// completionColor thresholds are unchanged (≥80 green, ≥50 amber, else red).

import React from "react";
import { useShiftSummary } from "../../../hooks/useShiftSummary";
import "../styles/shiftSummary.css";

// ── Skeleton placeholder ──────────────────────────────────────────────────────
//
// Renders four grey pulse bars in the same layout as a loaded pm-shift-band.
// Used for both the Shift Window and Job Completion sections while loading.

const BandSkeleton = () => (
  <div className="pm-shift-band pm-shift-band--skeleton">
    {[0, 1, 2, 3].map((i) => (
      <React.Fragment key={i}>
        <div className="pm-shift-time">
          <span className="pm-skeleton pm-skeleton--label" />
          <span className="pm-skeleton pm-skeleton--value" />
        </div>
        {i < 3 && <div className="pm-shift-divider" />}
      </React.Fragment>
    ))}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const ShiftSummary = ({ person }) => {
  const {
    shiftSummary: s,
    loading,
    error,
  } = useShiftSummary(person?.employeeNumber);

  // ── Derived completion percentage ─────────────────────────────────────────
  //
  // jobsAssigned is not in the API response; derive it as completed + inProgress.
  // Guard against divide-by-zero when no jobs have been assigned yet today.
  const jobsAssigned = s ? s.jobsCompleted + s.jobsInProgress : 0;
  const completionPct =
    jobsAssigned > 0 ? Math.round((s.jobsCompleted / jobsAssigned) * 100) : 0;

  const completionColor =
    completionPct >= 80
      ? "#38a169"
      : completionPct >= 50
        ? "#d69e2e"
        : "#cc0000";

  return (
    <>
      {/* Modal header — always rendered regardless of data state */}
      <p className="modal-description">
        Shift overview for {person.fullName} &mdash; {person.employeeNumber}{" "}
        &bull; {person.department}
      </p>

      {/* ── Error state ─────────────────────────────────────────────────── */}
      {error && (
        <div className="modal-section">
          <p className="pm-shift-error">{error}</p>
        </div>
      )}

      {/* ── Shift time band ──────────────────────────────────────────────── */}
      <div className="modal-section">
        <h3>Shift Window</h3>

        {loading ? (
          <BandSkeleton />
        ) : (
          <div className="pm-shift-band">
            <div className="pm-shift-time">
              <span className="pm-shift-time-label">Start</span>
              <span className="pm-shift-time-value">
                {s?.shiftStart ?? "—"}
              </span>
            </div>
            <div className="pm-shift-divider" />
            <div className="pm-shift-time">
              <span className="pm-shift-time-label">End</span>
              <span className="pm-shift-time-value">
                {/* shiftEnd is null while employee is still on shift */}
                {s?.shiftEnd ?? "—"}
              </span>
            </div>
            <div className="pm-shift-divider" />
            <div className="pm-shift-time">
              <span className="pm-shift-time-label">Active</span>
              <span className="pm-shift-time-value pm-shift-time-value--green">
                {s?.totalActiveTime ?? "0m"}
              </span>
            </div>
            <div className="pm-shift-divider" />
            <div className="pm-shift-time">
              <span className="pm-shift-time-label">Idle</span>
              <span className="pm-shift-time-value pm-shift-time-value--muted">
                {s?.totalIdleTime ?? "0m"}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── Job completion stats ─────────────────────────────────────────── */}
      <div className="modal-section">
        <h3>Job Completion</h3>

        {loading ? (
          <BandSkeleton />
        ) : (
          <div className="pm-shift-band">
            <div className="pm-shift-time">
              <span className="pm-shift-time-label">Completed</span>
              <span
                className="pm-shift-time-value pm-shift-time-value--green"
                style={{ color: completionColor }}
              >
                {s?.jobsCompleted ?? 0}
              </span>
            </div>
            <div className="pm-shift-divider" />
            <div className="pm-shift-time">
              <span className="pm-shift-time-label">In Progress</span>
              <span className="pm-shift-time-value pm-shift-time-value--orange">
                {s?.jobsInProgress ?? 0}
              </span>
            </div>
            <div className="pm-shift-divider" />
          </div>
        )}
      </div>

      {/* ── Stations visited ─────────────────────────────────────────────── */}
      <div className="modal-section">
        <h3>Stations Visited</h3>

        {loading ? (
          <div className="pm-stations-list pm-stations-list--skeleton">
            {[0, 1, 2].map((i) => (
              <span key={i} className="pm-skeleton pm-skeleton--tag" />
            ))}
          </div>
        ) : (
          <div className="pm-stations-list">
            {s?.stationsVisited?.length > 0 ? (
              s.stationsVisited.map((st, i) => (
                <span key={i} className="pm-station-tag">
                  {st}
                </span>
              ))
            ) : (
              // Empty state: no RTLS fence crossings recorded for today yet.
              <span className="pm-shift-time-value pm-shift-time-value--muted">
                None recorded
              </span>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ShiftSummary;
